import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { billingApi, isProduction } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { CheckIcon } from '@heroicons/react/24/outline';

interface PlanLimits {
  maxAffiliates: number;
  maxMonthlyTrackedRevenue: number;
  maxCampaigns: number;
  maxLinks: number;
  customDomain: boolean;
  apiAccess: boolean;
  webhooks: boolean;
  whiteLabel: boolean;
  prioritySupport: boolean;
  dedicatedManager: boolean;
  customIntegrations: boolean;
  advancedAnalytics: boolean;
  dataExports: boolean;
}

interface PricingPlan {
  tier: string;
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  platformFeePercent: number;
  limits: PlanLimits;
  features: string[];
  highlighted?: boolean;
}

const faqs = [
  {
    question: 'Can I switch plans anytime?',
    answer: 'Yes! Upgrade or downgrade at any time. When upgrading, you\'ll be prorated for the remainder of your billing cycle. Downgrades take effect at the next billing date.',
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards (Visa, Mastercard, Amex) and can arrange invoicing for Enterprise customers.',
  },
  {
    question: 'How do platform fees work?',
    answer: 'Platform fees are a small percentage of commissions paid out to affiliates. The fee decreases as you upgrade: 5% on Starter, 3% on Pro, 1% on Business, and 0% on Enterprise.',
  },
  {
    question: 'What happens when I hit my limits?',
    answer: 'You\'ll be prompted to upgrade when you approach your plan limits. We\'ll never delete your data or block existing affiliates—you\'ll just need to upgrade to add more.',
  },
  {
    question: 'Do you offer refunds?',
    answer: 'We offer a 14-day money-back guarantee on all paid plans. If it\'s not working for you, just reach out and we\'ll process your refund—no questions asked.',
  },
  {
    question: 'Can I use Venndela with my existing Stripe account?',
    answer: 'Absolutely. Venndela integrates directly with your Stripe account to track conversions and automate affiliate payouts.',
  },
];

export default function Pricing() {
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);

  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  // In production (VITE_COMING_SOON=true), show "Coming Soon" for paid plans
  const isComingSoonMode = import.meta.env.VITE_COMING_SOON === 'true';

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await billingApi.getPlans();
      if (response.data.success) {
        setPlans(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load plans:', error);
      toast.error('Failed to load pricing plans');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPlan = async (tier: string) => {
    if (tier === 'starter') {
      if (!isAuthenticated) {
        navigate(isProduction() ? '/coming-soon' : '/register');
      } else {
        navigate('/dashboard');
      }
      return;
    }

    if (tier === 'enterprise') {
      window.location.href = 'mailto:sales@venndela.com?subject=Enterprise%20Plan%20Inquiry';
      return;
    }

    if (!isAuthenticated) {
      navigate(isProduction() ? '/coming-soon' : `/register?plan=${tier}&interval=${billingInterval}`);
      return;
    }

    setCheckoutLoading(tier);
    try {
      const response = await billingApi.createCheckout(tier, billingInterval);
      if (response.data.success) {
        window.location.href = response.data.data.url;
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to create checkout session');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const formatPrice = (cents: number) => {
    if (cents === -1) return 'Custom';
    if (cents === 0) return 'Free';
    return `$${(cents / 100).toFixed(0)}`;
  };

  const getYearlyMonthlyPrice = (yearlyPrice: number) => {
    if (yearlyPrice === -1 || yearlyPrice === 0) return yearlyPrice;
    return Math.round(yearlyPrice / 12);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-rust/20 border-t-rust rounded-full animate-spin" />
      </div>
    );
  }

  // Find specific plans for bento layout (4 tiers: starter, pro, business, enterprise)
  const starterPlan = plans.find(p => p.tier === 'starter');
  const proPlan = plans.find(p => p.highlighted) || plans.find(p => p.tier === 'pro');
  const businessPlan = plans.find(p => p.tier === 'business');
  const enterprisePlan = plans.find(p => p.tier === 'enterprise');

  return (
    <div className="min-h-screen bg-cream">
      {/* Navigation */}
      <nav className="bg-charcoal px-6 lg:px-16 py-6 flex justify-between items-center">
        <Link to="/" className="font-display text-2xl font-bold text-warm-white tracking-tight">
          venndela
        </Link>
        <ul className="hidden md:flex items-center gap-12">
          <li>
            <Link to="/" className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
              Features
            </Link>
          </li>
          <li>
            <Link to="/pricing" className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
              Pricing
            </Link>
          </li>
          <li>
            <a href="#" className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
              Docs
            </a>
          </li>
          {isAuthenticated ? (
            <li>
              <Link to="/dashboard" className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
                Dashboard
              </Link>
            </li>
          ) : (
            <li>
              <Link to="/login" className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
                Log In
              </Link>
            </li>
          )}
        </ul>
      </nav>

      {/* Hero */}
      <section className="bg-charcoal px-6 lg:px-16 pt-12 pb-24 text-center relative overflow-hidden">
        <div className="absolute -top-full left-1/2 -translate-x-1/2 w-[150%] h-[200%] bg-gradient-sage opacity-10" />

        <p className="section-label relative z-10">Pricing</p>
        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-warm-white leading-tight mb-4 relative z-10">
          Simple pricing,<br /><em className="text-rust italic">serious growth</em>
        </h1>
        <p className="text-tan text-lg max-w-lg mx-auto relative z-10">
          Start free, scale as you grow. No hidden fees, no surprises.
        </p>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mt-10 relative z-10">
          <span className={`text-sm ${billingInterval === 'monthly' ? 'text-warm-white' : 'text-tan'}`}>
            Monthly
          </span>
          <button
            onClick={() => setBillingInterval(billingInterval === 'monthly' ? 'yearly' : 'monthly')}
            className={`w-14 h-8 rounded-full bg-rust relative transition-colors ${
              billingInterval === 'yearly' ? 'bg-sage' : ''
            }`}
          >
            <span
              className={`absolute top-1 left-1 w-6 h-6 bg-warm-white rounded-full transition-transform ${
                billingInterval === 'yearly' ? 'translate-x-6' : ''
              }`}
            />
          </button>
          <span className={`text-sm ${billingInterval === 'yearly' ? 'text-warm-white' : 'text-tan'}`}>
            Annual
          </span>
          <span className="bg-sage text-warm-white text-xs font-semibold px-3 py-1 rounded-full">
            Save 20%
          </span>
        </div>
      </section>

      {/* Bento Pricing Grid */}
      <section className="px-6 lg:px-16 -mt-8 relative z-10 pb-24">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Starter Card */}
          {starterPlan && (
            <div className="bg-warm-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <p className="font-display text-sm font-bold uppercase tracking-wide mb-2">{starterPlan.name}</p>
              <p className="text-charcoal/60 text-sm mb-6">{starterPlan.description}</p>
              <p className="font-serif text-5xl mb-1">
                {formatPrice(billingInterval === 'yearly' ? getYearlyMonthlyPrice(starterPlan.yearlyPrice) : starterPlan.monthlyPrice)}
                {starterPlan.monthlyPrice > 0 && <span className="font-sans text-base text-charcoal/60">/mo</span>}
              </p>
              <p className="text-sm text-charcoal/60 mb-6">Free forever</p>
              <ul className="space-y-3 mb-8">
                {starterPlan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-sm border-b border-tan/10 pb-3">
                    <span className="text-sage font-bold">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleSelectPlan(starterPlan.tier)}
                disabled={checkoutLoading === starterPlan.tier}
                className="w-full btn-charcoal py-3"
              >
                {checkoutLoading === starterPlan.tier ? 'Loading...' : 'Get Started Free'}
              </button>
            </div>
          )}

          {/* Pro Card (Featured) */}
          {proPlan && (
            <div className="bg-charcoal text-warm-white p-8 lg:row-span-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl relative">
              <span className="absolute top-6 right-6 bg-rust text-warm-white text-xs font-semibold px-3 py-1 uppercase tracking-wide">
                Most Popular
              </span>
              <p className="font-display text-sm font-bold uppercase tracking-wide mb-2">{proPlan.name}</p>
              <p className="text-tan text-sm mb-6">{proPlan.description}</p>
              <p className="font-serif text-5xl mb-1">
                {formatPrice(billingInterval === 'yearly' ? getYearlyMonthlyPrice(proPlan.yearlyPrice) : proPlan.monthlyPrice)}
                {proPlan.monthlyPrice > 0 && <span className="font-sans text-base text-tan">/mo</span>}
              </p>
              <p className="text-sm text-tan mb-6">
                {billingInterval === 'yearly' ? 'Billed annually' : 'Billed monthly'}
              </p>
              <ul className="space-y-3 mb-8">
                {proPlan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-sm border-b border-white/10 pb-3">
                    <span className="text-sage font-bold">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
              {isComingSoonMode ? (
                <button
                  disabled
                  className="w-full btn-primary py-3 opacity-75 cursor-not-allowed"
                >
                  Coming Soon
                </button>
              ) : (
                <button
                  onClick={() => handleSelectPlan(proPlan.tier)}
                  disabled={checkoutLoading === proPlan.tier}
                  className="w-full btn-primary py-3"
                >
                  {checkoutLoading === proPlan.tier ? 'Loading...' : 'Start 14-Day Trial'}
                </button>
              )}
            </div>
          )}

          {/* Business Card */}
          {businessPlan && (
            <div className="bg-warm-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <p className="font-display text-sm font-bold uppercase tracking-wide mb-2">{businessPlan.name}</p>
              <p className="text-charcoal/60 text-sm mb-6">{businessPlan.description}</p>
              <p className="font-serif text-5xl mb-1">
                {formatPrice(billingInterval === 'yearly' ? getYearlyMonthlyPrice(businessPlan.yearlyPrice) : businessPlan.monthlyPrice)}
                {businessPlan.monthlyPrice > 0 && <span className="font-sans text-base text-charcoal/60">/mo</span>}
              </p>
              <p className="text-sm text-charcoal/60 mb-6">
                {billingInterval === 'yearly' ? 'Billed annually' : 'Billed monthly'}
              </p>
              <ul className="space-y-3 mb-8">
                {businessPlan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-sm border-b border-tan/10 pb-3">
                    <span className="text-sage font-bold">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
              {isComingSoonMode ? (
                <button
                  disabled
                  className="w-full btn-charcoal py-3 opacity-75 cursor-not-allowed"
                >
                  Coming Soon
                </button>
              ) : (
                <button
                  onClick={() => handleSelectPlan(businessPlan.tier)}
                  disabled={checkoutLoading === businessPlan.tier}
                  className="w-full btn-charcoal py-3"
                >
                  {checkoutLoading === businessPlan.tier ? 'Loading...' : 'Start 14-Day Trial'}
                </button>
              )}
            </div>
          )}

          {/* Enterprise Card */}
          {enterprisePlan && (
            <div className="bg-warm-white p-8 lg:col-span-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="md:pr-8 md:border-r border-tan/20">
                  <p className="font-display text-sm font-bold uppercase tracking-wide mb-2">{enterprisePlan.name}</p>
                  <p className="text-charcoal/60 text-sm mb-6">{enterprisePlan.description}</p>
                  <p className="font-serif text-5xl mb-1">Custom</p>
                  <p className="text-sm text-charcoal/60 mb-6">Let's talk</p>
                  {isComingSoonMode ? (
                    <button
                      disabled
                      className="btn-charcoal py-3 px-8 opacity-75 cursor-not-allowed"
                    >
                      Coming Soon
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSelectPlan(enterprisePlan.tier)}
                      className="btn-charcoal py-3 px-8"
                    >
                      Contact Sales
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {enterprisePlan.features.slice(0, 6).map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <span className="text-sage font-bold">✓</span>
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>
      </section>

      {/* Comparison Table */}
      <section className="bg-warm-white py-24 px-6 lg:px-16">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-3xl text-center mb-12">Compare plans</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-tan/20">
                  <th className="text-left py-4 px-4 font-display text-sm font-bold uppercase tracking-wide">Feature</th>
                  {plans.map((plan) => (
                    <th key={plan.tier} className="py-4 px-4 text-center font-display text-sm font-bold uppercase tracking-wide">
                      {plan.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="bg-cream">
                  <td colSpan={plans.length + 1} className="py-3 px-4 font-display text-xs font-bold uppercase tracking-wider text-rust">
                    Affiliates & Tracking
                  </td>
                </tr>
                <tr className="border-b border-tan/10">
                  <td className="py-4 px-4 text-sm">Number of affiliates</td>
                  {plans.map((plan) => (
                    <td key={plan.tier} className="py-4 px-4 text-center text-sm">
                      {plan.limits.maxAffiliates === -1 ? 'Unlimited' : plan.limits.maxAffiliates}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-tan/10">
                  <td className="py-4 px-4 text-sm">Monthly Tracked Revenue</td>
                  {plans.map((plan) => (
                    <td key={plan.tier} className="py-4 px-4 text-center text-sm">
                      {plan.limits.maxMonthlyTrackedRevenue === -1
                        ? 'Unlimited'
                        : `$${(plan.limits.maxMonthlyTrackedRevenue / 100).toLocaleString()}`}
                    </td>
                  ))}
                </tr>
                <tr className="bg-cream">
                  <td colSpan={plans.length + 1} className="py-3 px-4 font-display text-xs font-bold uppercase tracking-wider text-rust">
                    Features
                  </td>
                </tr>
                <tr className="border-b border-tan/10">
                  <td className="py-4 px-4 text-sm">Custom Domain</td>
                  {plans.map((plan) => (
                    <td key={plan.tier} className="py-4 px-4 text-center">
                      {plan.limits.customDomain ? (
                        <CheckIcon className="w-5 h-5 text-sage mx-auto" />
                      ) : (
                        <span className="text-charcoal/30">—</span>
                      )}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-tan/10">
                  <td className="py-4 px-4 text-sm">API Access</td>
                  {plans.map((plan) => (
                    <td key={plan.tier} className="py-4 px-4 text-center">
                      {plan.limits.apiAccess ? (
                        <CheckIcon className="w-5 h-5 text-sage mx-auto" />
                      ) : (
                        <span className="text-charcoal/30">—</span>
                      )}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-tan/10">
                  <td className="py-4 px-4 text-sm">White Label</td>
                  {plans.map((plan) => (
                    <td key={plan.tier} className="py-4 px-4 text-center">
                      {plan.limits.whiteLabel ? (
                        <CheckIcon className="w-5 h-5 text-sage mx-auto" />
                      ) : (
                        <span className="text-charcoal/30">—</span>
                      )}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-tan/10">
                  <td className="py-4 px-4 text-sm">Advanced Analytics</td>
                  {plans.map((plan) => (
                    <td key={plan.tier} className="py-4 px-4 text-center">
                      {plan.limits.advancedAnalytics ? (
                        <CheckIcon className="w-5 h-5 text-sage mx-auto" />
                      ) : (
                        <span className="text-charcoal/30">—</span>
                      )}
                    </td>
                  ))}
                </tr>
                <tr className="bg-cream">
                  <td colSpan={plans.length + 1} className="py-3 px-4 font-display text-xs font-bold uppercase tracking-wider text-rust">
                    Support
                  </td>
                </tr>
                <tr className="border-b border-tan/10">
                  <td className="py-4 px-4 text-sm">Priority Support</td>
                  {plans.map((plan) => (
                    <td key={plan.tier} className="py-4 px-4 text-center">
                      {plan.limits.prioritySupport ? (
                        <CheckIcon className="w-5 h-5 text-sage mx-auto" />
                      ) : (
                        <span className="text-charcoal/30">—</span>
                      )}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-tan/10">
                  <td className="py-4 px-4 text-sm">Dedicated Manager</td>
                  {plans.map((plan) => (
                    <td key={plan.tier} className="py-4 px-4 text-center">
                      {plan.limits.dedicatedManager ? (
                        <CheckIcon className="w-5 h-5 text-sage mx-auto" />
                      ) : (
                        <span className="text-charcoal/30">—</span>
                      )}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 lg:px-16 bg-cream">
        <h2 className="font-serif text-3xl text-center mb-12">Frequently asked questions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {faqs.map((faq, idx) => (
            <div key={idx} className="bg-warm-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
              <h3 className="font-display font-bold text-lg mb-3">{faq.question}</h3>
              <p className="text-charcoal/70 leading-relaxed">{faq.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-charcoal py-24 px-6 lg:px-16 text-center relative overflow-hidden">
        <div className="absolute bottom-[-50%] left-1/2 -translate-x-1/2 w-[150%] h-full bg-gradient-rust opacity-10" />
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4 relative z-10">
          Ready to turn customers into advocates?
        </h2>
        <p className="text-tan text-lg mb-8 relative z-10">
          Start your free trial today. No credit card required.
        </p>
        <Link to={isProduction() ? '/coming-soon' : '/register'} className="btn-primary px-8 py-4 text-lg relative z-10">
          Get Started Free
        </Link>
      </section>

      {/* Footer */}
      <footer className="bg-charcoal border-t border-white/10 py-12 px-6 lg:px-16">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/" className="font-display text-xl font-bold text-warm-white">
            venndela
          </Link>
          <ul className="flex flex-wrap justify-center gap-8">
            <li>
              <Link to="/" className="text-tan text-sm hover:text-warm-white transition-colors">
                Features
              </Link>
            </li>
            <li>
              <Link to="/pricing" className="text-tan text-sm hover:text-warm-white transition-colors">
                Pricing
              </Link>
            </li>
            <li>
              <a href="#" className="text-tan text-sm hover:text-warm-white transition-colors">
                Docs
              </a>
            </li>
            <li>
              <a href="#" className="text-tan text-sm hover:text-warm-white transition-colors">
                Blog
              </a>
            </li>
            <li>
              <a href="mailto:support@venndela.com" className="text-tan text-sm hover:text-warm-white transition-colors">
                Contact
              </a>
            </li>
          </ul>
          <p className="text-charcoal/50 text-sm">
            © {new Date().getFullYear()} Venndela. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
